/** SUNTRONIX ONE — Finance → Tally Reconciliation. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, AlertTriangle, Copy, XCircle, HelpCircle, RefreshCw } from "lucide-react";

const STATES = [
  { key: "MATCHED", label: "Matched", icon: CheckCircle2, tint: "bg-emerald-100 text-emerald-800" },
  { key: "MISSING", label: "Missing", icon: HelpCircle, tint: "bg-amber-100 text-amber-800" },
  { key: "AMOUNT_MISMATCH", label: "Amount Mismatch", icon: AlertTriangle, tint: "bg-orange-100 text-orange-800" },
  { key: "DUPLICATE", label: "Duplicate", icon: Copy, tint: "bg-violet-100 text-violet-800" },
  { key: "SYNC_FAILED", label: "Sync Failed", icon: XCircle, tint: "bg-rose-100 text-rose-800" },
];

export default function TallyReconciliation() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("MATCHED");
  const [entity, setEntity] = useState("");

  const load = useCallback(() => {
    api.get(`/tally/reconciliation${entity ? `?entity=${entity}` : ""}`).then(({ data }) => setData(data)).catch(() => {});
  }, [entity]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.buckets?.[active] || [];

  return (
    <div className="mx-auto max-w-[1100px] space-y-4" data-testid="tally-recon-page">
      <header className="flex flex-wrap items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-teal-900 text-white"><CheckCircle2 className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">Tally Reconciliation</div>
          <div className="text-[11px] text-slate-500">ONE ↔ Tally posting reconciliation · {data?.total_mappings ?? 0} mappings</div>
        </div>
        <select value={entity} onChange={(e) => setEntity(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-[11px]" data-testid="recon-entity">
          <option value="">All entities</option>
          {["CUSTOMER", "VENDOR", "PRODUCT", "INVOICE", "PAYMENT", "CREDIT_NOTE", "DEBIT_NOTE"].map((e) => <option key={e}>{e}</option>)}
        </select>
        <button onClick={load} className="text-slate-400 hover:text-slate-700"><RefreshCw className="h-4 w-4" /></button>
      </header>

      {data?.pending_approval > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-[12px] text-blue-800" data-testid="recon-pending-approval">
          {data.pending_approval} high-impact posting(s) awaiting ONE Approval.
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="recon-buckets">
        {STATES.map((s) => {
          const Icon = s.icon; const n = data?.counts?.[s.key] ?? 0;
          return (
            <button key={s.key} onClick={() => setActive(s.key)} data-testid={`recon-bucket-${s.key}`}
              className={`rounded-xl border p-3 text-left transition-colors ${active === s.key ? "border-teal-500 ring-1 ring-teal-300" : "border-slate-200"}`}>
              <div className={`inline-grid h-7 w-7 place-items-center rounded-lg ${s.tint}`}><Icon className="h-3.5 w-3.5" /></div>
              <div className="mt-1.5 text-xl font-semibold text-slate-900">{n}</div>
              <div className="text-[9.5px] uppercase tracking-wide text-slate-500">{s.label}</div>
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="recon-table">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
            <tr><th className="px-3 py-2">Entity</th><th className="px-3 py-2">ONE id</th><th className="px-3 py-2">Tally id</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">ONE amt</th><th className="px-3 py-2">Tally amt</th></tr>
          </thead>
          <tbody>
            {active === "DUPLICATE" ? rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100"><td className="px-3 py-1.5" colSpan={2}>{r.key}</td><td className="px-3 py-1.5 font-mono text-[9px]" colSpan={4}>{(r.tally_ids || []).join(", ")}</td></tr>
            )) : rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-3 py-1.5">{r.entity}</td>
                <td className="px-3 py-1.5 font-mono text-[9px]">{r.one_id}</td>
                <td className="px-3 py-1.5 font-mono text-[9px]">{r.tally_id || "—"}</td>
                <td className="px-3 py-1.5">{r.name || "—"}</td>
                <td className="px-3 py-1.5">{r.amount ?? "—"}</td>
                <td className={`px-3 py-1.5 ${r.tally_amount != null && r.tally_amount !== r.amount ? "font-semibold text-orange-700" : ""}`}>{r.tally_amount ?? (r.message || "—")}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Nothing in this bucket. 🎉</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
